import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { getSocket } from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { setViewAsHeader } from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	// feat_299 — objeto completo del usuario "visto como" (null = modo normal).
	// Persistencia en memoria: un reload vuelve a la vista del admin.
	const [viewAsUser, setViewAsUser] = useState(null);

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token) {
				config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
				setIsAuth(true);
			}
			return config;
		},
		error => {
			Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;
			if (error?.response?.status === 403 && !originalRequest._retry) {
				originalRequest._retry = true;

				// feat_239: si hay wl_refresh_token, usar explicit DIRECTAMENTE (NO
				// intentar la cookie primero). En dominios white-label cross-site la
				// cookie jrt de terceros está bloqueada → refresh_token (cookie) da 401
				// → el response-interceptor (abajo) hace removeItem(token)+setIsAuth(false)
				// = deslogueo transitorio; el fallback explicit lo recuperaba → oscilación
				// isAuth false↔true → loop infinito de login (feat_238). La cookie sigue
				// siendo el camino cuando NO hay wl token (sesión same-site clásica).
				let data;
				// feat_246: durante impersonation, preferir el impersonating_refresh_token
				// (identidad impersonada) por sobre wl_refresh_token (identidad propia del
				// super/reseller, que sigue en localStorage). Sin esta precedencia, cuando
				// el access token impersonado expira, un 403 refrescaba con wl_refresh_token
				// → volvía a la identidad del super → "se mezcla todo, ve todas las empresas".
				// Mismo orden que el mount effect de abajo.
				const impersonatingRefreshToken = localStorage.getItem("impersonating_refresh_token");
				const wlRefreshToken = localStorage.getItem("wl_refresh_token");
				let usedImpersonation = false;
				if (impersonatingRefreshToken) {
					usedImpersonation = true;
					const resp = await api.post("/auth/refresh_token_explicit", {
						refreshToken: impersonatingRefreshToken
					});
					data = resp.data;
				} else if (wlRefreshToken) {
					const resp = await api.post("/auth/refresh_token_explicit", {
						refreshToken: wlRefreshToken
					});
					data = resp.data;
				} else {
					const resp = await api.post("/auth/refresh_token");
					data = resp.data;
				}
				if (data) {
					localStorage.setItem("token", JSON.stringify(data.token));
					if (data.refreshToken) {
						// feat_248: durante impersonation, rotar SOLO
						// impersonating_refresh_token. NUNCA pisar wl_refresh_token
						// (identidad del super/reseller) con el refresh del admin
						// impersonado → al "Volver a super" el mount effect refrescaba con
						// wl_refresh_token contaminado y re-hidrataba como el admin (menú de
						// empresa) mientras el token activo era el del super (datos
						// cross-tenant) = estado MIXTO reportado (feat_246/247 no lo cerraron
						// porque limpiaban impersonating_refresh_token pero wl ya estaba sucio).
						if (usedImpersonation) {
							localStorage.setItem("impersonating_refresh_token", data.refreshToken);
						} else {
							localStorage.setItem("wl_refresh_token", data.refreshToken);
						}
					}
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}
				return api(originalRequest);
			}
			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					// When impersonating, prefer the explicit refresh token stored in
					// localStorage over the jrt cookie to avoid cookie-override races
					// where the old superadmin cookie survives and returns the wrong user.
					const impersonatingRefreshToken = localStorage.getItem("impersonating_refresh_token");
					let data;
					let usedImpersonation = false;
					if (impersonatingRefreshToken) {
						usedImpersonation = true;
						const resp = await api.post("/auth/refresh_token_explicit", {
							refreshToken: impersonatingRefreshToken
						});
						data = resp.data;
					} else {
						// feat_239: wl_refresh_token presente → explicit directo (evita el
						// 401 de la cookie cross-site que dispara el deslogueo transitorio
						// y el loop; ver comentario en el response-interceptor arriba).
						const wlRefreshToken = localStorage.getItem("wl_refresh_token");
						if (wlRefreshToken) {
							const resp = await api.post("/auth/refresh_token_explicit", {
								refreshToken: wlRefreshToken
							});
							data = resp.data;
						} else {
							const resp = await api.post("/auth/refresh_token");
							data = resp.data;
						}
					}
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					// Rotar el token guardado (el backend devuelve uno nuevo).
					// feat_248: durante impersonation rotar SOLO impersonating_refresh_token
					// → wl_refresh_token conserva la identidad del super, sino "Volver a
					// super" re-hidrataba como el admin impersonado (estado mixto).
					if (data.refreshToken) {
						if (usedImpersonation) {
							localStorage.setItem("impersonating_refresh_token", data.refreshToken);
						} else {
							localStorage.setItem("wl_refresh_token", data.refreshToken);
						}
					}
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = getSocket();

		const handleUser = data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		};
		socket.on("user", handleUser);

		return () => {
			// SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
			socket.off("user", handleUser);
		};
	}, [user]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			// feat_238 (B1): guardar el refreshToken para el fallback cross-site.
			if (data.refreshToken) {
				localStorage.setItem("wl_refresh_token", data.refreshToken);
			}
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push(data.user.isSuperAdmin ? "/superadmin" : "/dashboard");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	// feat_299 — activar "Ver como usuario". GET /users/:id ya existe y viene
	// scopeado por companyId; trae queues + ticketChipsVisibility + flags.
	const activateViewAs = async targetUserId => {
		try {
			const { data } = await api.get(`/users/${targetUserId}`);
			setViewAsHeader(targetUserId);
			setViewAsUser(data);
			toast.success(i18n.t("viewAs.activated", { name: data.name }));
		} catch (err) {
			setViewAsHeader(null);
			setViewAsUser(null);
			toastError(err);
		}
	};

	const exitViewAs = () => {
		setViewAsHeader(null);
		setViewAsUser(null);
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			// feat_299: nunca arrastrar el modo view-as a la próxima sesión.
			setViewAsHeader(null);
			setViewAsUser(null);
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			localStorage.removeItem("wl_refresh_token");
			localStorage.removeItem("lastInternalUser");
			// feat_247: si el logout ocurre desde una vista impersonada, limpiar también
			// el estado de impersonation → el próximo login no hereda una sesión mixta.
			localStorage.removeItem("impersonating_refresh_token");
			localStorage.removeItem("token_superadmin_backup");
			localStorage.removeItem("impersonating_company");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return {
		isAuth,
		// feat_299 — el `user` del contexto ES el target cuando view-as está
		// activo: con eso flipean solos <Can>, sidebar, chips y la campanita.
		user: viewAsUser || user,
		realUser: user, // identidad verdadera (escrituras, banner, gates del menú)
		viewAsUser,
		activateViewAs,
		exitViewAs,
		setAuthUser: setUser,
		loading,
		handleLogin,
		handleLogout
	};
};

export default useAuth;
